import { useEffect, useMemo, useState } from 'react';
import { Button, useDisclosure } from '@ds';
import { MdAltRoute } from 'react-icons/md';
import { ViewRouteSchemaModal } from '@lp/features/RoutePlanner/components/RouteModals/components/ViewRouteSchemaModal';
import { RouteSchemaType } from '@lp/features/RoutePlanner';
import { convertRouteList, getRouteSegments } from '@lp/features/RoutePlanner/utils';
import { useRouteDetails } from '@lp/features/RoutePlanner/hooks/useRouteDetails';
import { useRouteLocations } from '@lp/features/RoutePlanner/hooks/useRouteLocations';
import { useCaseDetails } from '@/hooks/useCaseDetails';
import {
  AircraftLegResource,
  AviaLegTypes,
  BaseLegResourceType,
  GroundLegTypes,
  HubRouteLocation,
  RouteTypeEnum,
} from '@/api/apis/route';
import { Case, CaseResourceType } from '@/api/apis/case';
import { RouteSegmentType } from '@/types/route';

export const CaseLogisticsRouteSchema = () => {
  const [segments, setSegments] = useState<RouteSegmentType[]>([]);
  const [legs, setLegs] = useState<Map<string, (AviaLegTypes | GroundLegTypes)[]>>(new Map());
  const { isOpen, onClose, onOpen } = useDisclosure();
  const {
    donorHospitalDetails: donorHospital,
    txCenterDetails: txCenter,
    organType,
    resources,
  } = useCaseDetails() as Case;
  const mainRoute = useRouteDetails(RouteTypeEnum.Main);
  const { list, getLocationById } = useRouteLocations();

  const aircraftsList = useMemo(() => {
    const aircrafts = resources.filter((r) => r.resourceType === CaseResourceType.Aircraft);
    return (
      aircrafts?.map<BaseLegResourceType<AircraftLegResource>>((a) => ({
        id: a.resourceId,
        type: CaseResourceType.Aircraft,
        data: a.data,
      })) || []
    );
  }, [resources]);

  useEffect(() => {
    const defaultSegments = getRouteSegments({
      donorHospital: donorHospital!,
      txCenter,
      legs: mainRoute.legs,
      arrivalHub: getLocationById(mainRoute.arrivalHub?.locationId) as HubRouteLocation,
      departureHub: getLocationById(mainRoute.departureHub?.locationId) as HubRouteLocation,
    });
    const { segments: newSegments, legs: newLegs } = convertRouteList(defaultSegments, mainRoute.legs || []);

    setSegments(newSegments);
    setLegs(newLegs);
  }, [donorHospital, getLocationById, mainRoute, txCenter]);

  const routeSchemaConfig = useMemo(() => {
    return {
      organType,
      segments,
      legs,
      aircrafts: aircraftsList,
      routeType: 'full',
      locations: list,
    } as RouteSchemaType;
  }, [aircraftsList, legs, organType, segments, list]);

  return (
    <>
      <Button variant="light" size="sm" onPress={onOpen}>
        <MdAltRoute />
      </Button>
      {isOpen && (
        <ViewRouteSchemaModal title="Route Schema" route={routeSchemaConfig} isOpen={isOpen} onClose={onClose} />
      )}
    </>
  );
};
